// Expanded stage details panel inside the SpoolCard. Renders the
// prototype's STAGES × (Start / Est hrs / Finish / Assigned / Status)
// grid, plus the "Show all stages" toggle that flips between the
// active 10-row view and the full 17-row view.
//
// Edits patch directly via patchSpoolStage so the parent's job
// payload stays in sync with the DB without needing a Save click.
import { useMemo } from "react";
import {
  STAGE_STATUSES,
  type SpoolStageDto,
  type StageDefDto,
  type StageKey,
  type StageStatus,
  type TeamMemberDto,
} from "../../../../../../shared/types/production-schedule";
import { fmtDate } from "../lib/dates";
import { planBaySwap, stageFinish, visibleStageRows } from "../lib/stageHelpers";
import { useActiveStages } from "./StagesProvider";
import { BayPicker } from "./BayPicker";

interface Props {
  stages: SpoolStageDto[];
  team: TeamMemberDto[];
  weldBayKey: StageKey | null;
  showAll: boolean;
  onToggleShowAll(): void;
  // Multi-bay mode (Simple-view job pipeline). A spool welds in
  // exactly one bay so its picker swaps; a job can weld in several
  // bays at once, so the picker toggles bays on/off instead and the
  // table shows every non-skipped weld row, not just the chosen one.
  multiBay?: boolean;
  onStagePatch(
    stageKey: StageKey,
    patch: {
      hours?: number;
      assigneeId?: number | null;
      startDate?: string | null;
      doneDate?: string | null;
      status?: StageStatus;
    },
  ): void | Promise<void>;
}

function hideEyeSvg(open: boolean) {
  if (open) {
    return (
      <svg viewBox="0 0 24 24" width="14" height="14" fill="none"
        stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
        <circle cx="12" cy="12" r="3"/>
      </svg>
    );
  }
  return (
    <svg viewBox="0 0 24 24" width="14" height="14" fill="none"
      stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/>
      <line x1="1" y1="1" x2="23" y2="23"/>
    </svg>
  );
}

export function SpoolStageDetailsTable({
  stages,
  team,
  weldBayKey,
  showAll,
  onToggleShowAll,
  multiBay = false,
  onStagePatch,
}: Props) {
  const catalogue = useActiveStages();
  const stageByKey = useMemo(() => {
    const m = new Map<string, StageDefDto>();
    for (const s of catalogue) m.set(s.stageKey, s);
    return m;
  }, [catalogue]);
  const stageBySpoolKey = useMemo(() => {
    const m = new Map<string, SpoolStageDto>();
    for (const s of stages) m.set(s.stageKey, s);
    return m;
  }, [stages]);
  // Multi-bay rows skip the chosen-bay collapse — every non-skipped
  // weld bay shows (the caller passes dense rows in catalogue
  // order). Single mode keeps the one-weld-pill behaviour.
  const rows = useMemo(
    () => multiBay
      ? stages.filter((s) => showAll || s.status !== "skipped")
      : visibleStageRows(stages, weldBayKey, showAll, catalogue),
    [stages, weldBayKey, showAll, catalogue, multiBay],
  );
  const activeBayKeys = useMemo(
    () => new Set(
      stages
        .filter((s) => s.stageKey.startsWith("weldbay") && s.status !== "skipped")
        .map((s) => s.stageKey),
    ),
    [stages],
  );

  // Multi-bay toggle: clicking a bay flips it active ↔ skipped;
  // the "—" button skips every active bay. No tracking migration —
  // each bay row keeps its own hours/dates.
  const toggleBay = async (key: StageKey | null) => {
    if (key === null) {
      for (const s of stages) {
        if (s.stageKey.startsWith("weldbay") && s.status !== "skipped") {
          await onStagePatch(s.stageKey as StageKey, { status: "skipped" });
        }
      }
      return;
    }
    const row = stageBySpoolKey.get(key);
    if (!row || row.status === "skipped") {
      await onStagePatch(key, { status: "pending" });
    } else {
      await onStagePatch(key, { status: "skipped" });
    }
  };

  // Swapping bays must not move the spool's progress — it only
  // relabels which bay the single weld stage runs in. planBaySwap
  // migrates the active bay's status + start date / est hrs /
  // finish onto the new bay and clears + hides the old one, so the
  // tracking follows the selection. Capture the active row up front
  // so the plan uses pre-swap values no matter what order the
  // refetch-triggering patches land in.
  const swapBay = async (nextKey: StageKey | null) => {
    const current = weldBayKey ? stageBySpoolKey.get(weldBayKey) ?? null : null;
    const patches = planBaySwap({ fromKey: weldBayKey, toKey: nextKey, current });
    for (const p of patches) {
      await onStagePatch(p.stageKey, p.patch);
    }
  };

  return (
    <div className="stages-wrap">
      <div className="stages-wrap-head">
        <div className="stages-wrap-title">
          Stage details{showAll ? "" : ` · showing ${rows.length} of ${catalogue.length}`}
        </div>
        <button
          type="button"
          className={`eye-toggle${showAll ? " active" : ""}`}
          onClick={onToggleShowAll}
          title={showAll ? "Hide skipped stages" : "Show all stages"}
        >
          {hideEyeSvg(!showAll)}
          {showAll ? "Showing all" : "Show all stages"}
        </button>
      </div>
      <BayPicker
        chosen={weldBayKey}
        activeKeys={multiBay ? activeBayKeys : undefined}
        catalogue={catalogue}
        onPick={(k) => void (multiBay ? toggleBay(k) : swapBay(k))}
      />
      <table className="stages-table">
        <thead>
          <tr>
            <th style={{ width: 30 }} />
            <th style={{ width: 160 }}>Stage</th>
            <th>Start date</th>
            <th>Est hrs</th>
            <th>Finish</th>
            <th>Assigned to</th>
            <th>Status</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((st) => {
            const def = stageByKey.get(st.stageKey);
            if (!def) return null;
            const rowCls = st.status === "skipped"
              ? "is-skipped"
              : st.status === "complete"
                ? "is-complete"
                : "";
            const finish = stageFinish(st);
            return (
              <tr key={st.stageKey} className={rowCls}>
                <td style={{ textAlign: "center" }}>
                  <button
                    type="button"
                    className="stage-hide-btn"
                    title={st.status === "skipped" ? "Restore stage" : "Skip stage"}
                    onClick={() => void onStagePatch(st.stageKey as StageKey, {
                      status: st.status === "skipped" ? "pending" : "skipped",
                    })}
                  >
                    {hideEyeSvg(st.status !== "skipped")}
                  </button>
                </td>
                <td>
                  <div className="stage-name-cell">
                    <span className="stage-tag" style={{ background: def.color }}>{def.short}</span>
                    {def.name}
                  </div>
                </td>
                <td>
                  <input
                    type="date"
                    className="stage-inp w-date"
                    value={st.startDate ?? ""}
                    onChange={(e) => void onStagePatch(st.stageKey as StageKey, {
                      startDate: e.target.value || null,
                    })}
                  />
                </td>
                <td>
                  {def.hasHours ? (
                    <input
                      type="number"
                      min={0}
                      step="0.5"
                      className="stage-inp w-hrs"
                      value={st.hours === 0 ? "" : st.hours}
                      placeholder="0"
                      onChange={(e) => void onStagePatch(st.stageKey as StageKey, {
                        hours: Number(e.target.value) || 0,
                      })}
                    />
                  ) : (
                    <span style={{ color: "var(--bsp-fg-subtle)", fontFamily: "var(--bsp-font-mono)", fontSize: 11 }}>—</span>
                  )}
                </td>
                <td style={{ fontFamily: "var(--bsp-font-mono)", fontSize: 11, color: "var(--bsp-fg-muted)" }}>
                  {fmtDate(finish)}
                </td>
                <td>
                  <select
                    className="stage-inp w-asg"
                    value={st.assigneeId ?? ""}
                    onChange={(e) => void onStagePatch(st.stageKey as StageKey, {
                      assigneeId: e.target.value ? Number(e.target.value) : null,
                    })}
                  >
                    <option value="">— Unassigned —</option>
                    {team.map((m) => (
                      <option key={m.id} value={m.id}>{m.initials} · {m.name}</option>
                    ))}
                  </select>
                </td>
                <td>
                  <select
                    className="stage-inp w-stat"
                    value={st.status}
                    onChange={(e) => void onStagePatch(st.stageKey as StageKey, {
                      status: e.target.value as StageStatus,
                    })}
                  >
                    {STAGE_STATUSES.map((s) => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </select>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {team.length === 0 && (
        <div style={{
          marginTop: 8,
          fontSize: 11,
          color: "var(--bsp-fg-muted)",
          fontStyle: "italic",
        }}>
          Add team members in Manage Team & Assignees before you can assign stages.
        </div>
      )}
    </div>
  );
}
